import { useCallback, useEffect, useState, type FormEvent } from 'react'

import {
  ApiError,
  api,
  can,
  refusalReason,
  shortDate,
  type IssuedProcurementCredential,
  type ProcurementCredential,
} from '../../lib/api'
import {
  DELIVERY_NOUNS,
  EVENT_NOUNS,
  EVENT_WORDS,
  INCIDENT_EVENTS,
  REGISTRY_SCOPES,
  listWords,
  registryApi,
  scopesSentence,
  type RegistryDelivery,
  type RegistryEventType,
  type RegistryRequest,
  type RegistrySubscriber,
  type RegistryTest,
} from '../../lib/registry'
import { ErrorBox, Loading } from '../../components/DataTable'
import { Dialog, Status, Toggletip, type Tone } from '../../components/kit'
import { Icon } from '../../components/ui'
import { useSave } from '../../lib/save'
import '../../styles/integrations.css'
import '../../styles/sweep.css'
import '../../styles/registry.css'

// The vendor registry: where other systems read the vendor register from, and
// where its changes are sent.
//
// # WHAT THIS SCREEN HAS TO ANSWER
//
// Three questions, in the order an administrator asks them. Where do changes
// to our vendors go, and is each destination taking them? Which other systems
// may read the register or ask for a vendor? And what have they asked for?
// FOCTTA Core needs nothing from this screen: when TPRM is switched on in
// Core, Core sets its own destination and signs its own reads, and it shows
// here as one more destination so a person can see it working.
//
// # WHY A SECRET IS SHOWN ONCE
//
// A destination checks every delivery against the secret it was given. The
// secret is sealed here the moment it is made and cannot be read back, so it
// is shown in the drawer that made it, once, and never again.

function when(iso?: string): string {
  if (!iso) return ''
  const d = new Date(iso)
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      })
}

function describe(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.issues.length > 0) return err.issues.map((i) => i.detail).join(' ')
    return err.message
  }
  return err instanceof Error ? err.message : String(err)
}

function stateOf(s: RegistrySubscriber): { word: string; tone: Tone } {
  if (!s.enabled) return { word: 'Paused', tone: 'neutral' }
  const failedLast = s.lastFailedAt && (!s.lastDeliveredAt || s.lastFailedAt > s.lastDeliveredAt)
  if (s.dead > 0 || failedLast) return { word: 'Not working', tone: 'warn' }
  if (s.lastDeliveredAt) return { word: 'Working', tone: 'ok' }
  return { word: 'Not proved yet', tone: 'neutral' }
}

const DELIVERY_WORDS: Record<RegistryDelivery['status'], { word: string; tone: Tone }> = {
  PENDING: { word: 'Waiting', tone: 'neutral' },
  SENDING: { word: 'Sending', tone: 'info' },
  SENT: { word: 'Sent', tone: 'ok' },
  FAILED: { word: 'Will retry', tone: 'warn' },
  DEAD: { word: 'Gave up', tone: 'crit' },
}

const MATCH_WORDS: Record<RegistryRequest['matchedBy'], string> = {
  CREATED: 'New draft',
  EXTERNAL_REFERENCE: 'Their reference',
  PAN: 'PAN',
  GSTIN: 'GSTIN',
  CIN: 'CIN',
  LLPIN: 'LLPIN',
  LEI: 'LEI',
}

function sourceWords(s: string): string {
  return s === 'FOCTTA_CORE' ? 'FOCTTA Core' : s.replace(/_/g, ' ')
}

export function VendorRegistry() {
  const [subs, setSubs] = useState<RegistrySubscriber[] | null>(null)
  const [paired, setPaired] = useState(false)
  const [types, setTypes] = useState<RegistryEventType[]>([])
  const [requests, setRequests] = useState<RegistryRequest[] | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [adding, setAdding] = useState(false)

  const load = useCallback(() => {
    registryApi
      .subscribers()
      .then((r) => {
        setSubs(r.subscribers)
        setPaired(r.pairedWithCore)
        setTypes(r.eventTypes)
      })
      .catch(setError)
    registryApi
      .requests()
      .then((r) => setRequests(r.requests))
      .catch(() => setRequests([]))
  }, [])
  useEffect(load, [load])

  if (error != null) return <ErrorBox error={error} what="Vendor registry" />
  if (!subs) return <Loading what="the vendor registry" />

  const mayWrite = can('config.write')
  const core = subs.find((s) => s.kind === 'FOCTTA_SUITE')

  return (
    <div className="settings-stack">
      <section className="panel">
        <div className="p1-head">
          <div>
            <h2>Vendor registry</h2>
            <p className="obs-what">Other systems read your vendors from here and hear when they change</p>
          </div>
          <div className="p1-actions">
            <button
              type="button"
              className="button primary"
              disabled={!mayWrite}
              title={mayWrite ? undefined : refusalReason('config.write')}
              onClick={() => setAdding(true)}
            >
              Add a destination
            </button>
          </div>
        </div>
        <p className="note sw-row">
          {core ? (
            <>
              <Status tone="ok">FOCTTA Core</Status> Core reads this register and hears every change, with nothing to
              set up
            </>
          ) : paired ? (
            <>
              <Status tone="neutral">FOCTTA Core</Status> Paired. Core sets its own destination the next time TPRM is
              switched on there
            </>
          ) : (
            <>
              <Status tone="neutral">FOCTTA Core</Status> Not paired. Any other system can use a destination and a key
              below
            </>
          )}
          <Toggletip label="How other systems read the register">
            The register, its agreements, what each vendor processes and who sits behind it, in one published format. A
            destination is told when any of it changes. A key lets a system read it, or ask for a vendor to be added.
          </Toggletip>
        </p>
      </section>

      <AddDestination open={adding} types={types} onClose={() => setAdding(false)} onAdded={load} />

      {subs.length === 0 && (
        <section className="panel">
          <p className="empty">No destination yet, so changes to vendors are not sent anywhere</p>
        </section>
      )}
      {subs.map((s) => (
        <DestinationCard key={s.id} sub={s} onChanged={load} />
      ))}

      <Keys />

      <section className="panel">
        <div className="p1-head">
          <div>
            <h3>Vendors asked for</h3>
            <p className="muted">By FOCTTA Core and other systems</p>
          </div>
        </div>
        {requests === null ? (
          <Loading what="the requests" />
        ) : requests.length === 0 ? (
          <p className="empty">Nothing has been asked for yet</p>
        ) : (
          <div className="table-wrap">
            <table className="table vr-table">
              <thead>
                <tr>
                  <th scope="col">Vendor</th>
                  <th scope="col">From</th>
                  <th scope="col" className="vr-wide">
                    Found by
                  </th>
                  <th scope="col">Stage</th>
                  <th scope="col" className="vr-wide">
                    When
                  </th>
                </tr>
              </thead>
              <tbody>
                {requests.map((r) => (
                  <tr key={r.requestId}>
                    <td>
                      <a className="cell-primary" href={'#/vendor/' + encodeURIComponent(r.vendorId)}>
                        {r.legalName}
                      </a>
                      <span className="int-sub">{r.vendorCode}</span>
                    </td>
                    <td>
                      {sourceWords(r.sourceSystem)}
                      <span className="int-sub mono vr-wide">{r.externalRequestId}</span>
                    </td>
                    <td className="vr-wide">{MATCH_WORDS[r.matchedBy] ?? r.matchedBy}</td>
                    <td>{r.status.charAt(0) + r.status.slice(1).toLowerCase().replace(/_/g, ' ')}</td>
                    <td className="vr-wide">{when(r.lastReceivedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  )
}

function AddDestination({
  open,
  types,
  onClose,
  onAdded,
}: {
  open: boolean
  types: RegistryEventType[]
  onClose: () => void
  onAdded: () => void
}) {
  const [name, setName] = useState('')
  const [url, setUrl] = useState('')
  const [chosen, setChosen] = useState<RegistryEventType[]>([])
  const [secret, setSecret] = useState('')
  const [problem, setProblem] = useState('')
  const save = useSave(open, 'Add a destination')
  const busy = save.busy

  useEffect(() => {
    // The four vendor kinds are ticked; the incident kinds wait to be asked for.
    if (open) setChosen(types.filter((t) => !INCIDENT_EVENTS.includes(t)))
  }, [open, types])

  function close() {
    setName('')
    setUrl('')
    setSecret('')
    setProblem('')
    onClose()
  }

  function add(e: FormEvent) {
    e.preventDefault()
    setProblem('')
    save.send(registryApi.addSubscriber({ name, url, eventTypes: chosen }), {
      shown: (got) => setSecret(got.signingSecret),
      done: onAdded,
      failed: (err) => setProblem(describe(err)),
      lost: 'The destination was added, but its dialog was closed before the secret could be shown, and it cannot be shown again. Remove the destination and add it again.',
    })
  }


  // Two fields and a list of six to eight ticks: more than a card in the
  // middle of the window holds without scrolling, so it is the side panel.
  return (
    <Dialog
      shape="drawer"
      kind="form"
      open={open}
      onClose={close}
      title={secret ? 'Copy this secret now' : 'Add a destination'}
      busy={busy}
      // As a key: asked about before it closes, and acknowledged on the other
      // side from the button that made it.
      guard={secret ? 'Close without copying the secret? It cannot be shown again.' : undefined}
      more={
        secret ? (
          <button type="button" onClick={close}>
            I have copied it
          </button>
        ) : undefined
      }
      primary={
        secret
          ? undefined
          : {
              label: busy ? 'Adding' : 'Add and show the secret once',
              form: 'registry-destination-form',
              off: chosen.length === 0 && 'Tick at least one change to send',
              actionId: 'tprm.settings-vendor-registry.a01',
            }
      }
    >
      {secret ? (
        <div className="int-section" role="status">
          <p className="int-helper">It cannot be shown again</p>
          <p className="mono int-wrap int-secret">{secret}</p>
          <p className="int-prose">
            Every change is sent signed with it. The system at that address checks each one before it reads it.
          </p>
        </div>
      ) : (
        <form id="registry-destination-form" className="wizard-form int-form" onSubmit={add}>
          <label>
            Name
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Group GRC register" required />
          </label>
          <label>
            Address
            <input
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://grc.example.com/vendor-changes"
              inputMode="url"
              required
            />
            <span className="field-note">Where the system takes the changes. It must start https://</span>
          </label>
          <fieldset className="fieldset int-fieldset">
            <legend>Send it when</legend>
            {types.map((t) => (
              <label key={t} className="check">
                <input
                  type="checkbox"
                  checked={chosen.includes(t)}
                  onChange={(e) =>
                    setChosen((c) =>
                      e.target.checked ? types.filter((x) => c.includes(x) || x === t) : c.filter((x) => x !== t),
                    )
                  }
                />{' '}
                {EVENT_WORDS[t]}
              </label>
            ))}
          </fieldset>
          {problem && <p className="note warn">{problem}</p>}
        </form>
      )}
    </Dialog>
  )
}

function DestinationCard({ sub: s, onChanged }: { sub: RegistrySubscriber; onChanged: () => void }) {
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState('')
  const [info, setInfo] = useState('')
  const [test, setTest] = useState<RegistryTest | null>(null)
  const [deliveries, setDeliveries] = useState<RegistryDelivery[] | null>(null)
  const mayWrite = can('config.write')
  const state = stateOf(s)
  const isCore = s.kind === 'FOCTTA_SUITE'

  async function act(fn: () => Promise<void>) {
    setBusy(true)
    setMessage('')
    setInfo('')
    try {
      await fn()
    } catch (err) {
      setMessage(describe(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className="panel">
      <div className="p1-head">
        <div>
          <h3 className="sw-title">
            {s.name} <span className="chip">{isCore ? 'Signed with the suite key' : 'Another system'}</span>
            <Status tone={state.tone}>{state.word}</Status>
          </h3>
          <p className="muted mono" style={{ wordBreak: 'break-all' }}>
            {s.url}
          </p>
        </div>
      </div>

      <div className="std-meta">
        <span className="chip">
          <Icon name="send" size={13} />{' '}
          {s.lastDeliveredAt ? 'Last taken ' + when(s.lastDeliveredAt) : 'Nothing taken yet'}
        </span>
        <span className="chip">{s.sent} sent</span>
        <span className={'chip' + (s.waiting > 0 ? ' strong' : '')}>{s.waiting} waiting</span>
        <span className={'chip' + (s.failing > 0 ? ' warn' : '')}>{s.failing} will retry</span>
        <span className={'chip' + (s.dead > 0 ? ' warn' : '')}>{s.dead} gave up</span>
      </div>
      <p className="field-note">Sends {listWords(s.eventTypes.map((t) => EVENT_NOUNS[t]))}</p>

      {s.lastError && state.tone === 'warn' && <p className="note warn">Last answer: {s.lastError}</p>}
      {message && <p className="note warn">{message}</p>}
      {info && <p className="note">{info}</p>}
      {test && (
        <p className={'note' + (test.answer === 'ok' ? ' ok' : ' warn')} role="status">
          <Status tone={test.answer === 'ok' ? 'ok' : test.answer === 'refused' ? 'crit' : 'warn'}>
            {test.answer === 'ok' ? 'Taken' : test.answer === 'refused' ? 'Refused' : 'Unproven'}
          </Status>{' '}
          {test.detail}
        </p>
      )}

      <div className="p1-actions">
        <button
          type="button"
          className="button"
          disabled={busy || !mayWrite}
          onClick={() =>
            void act(async () => {
              setTest(await registryApi.testSubscriber(s.id))
              onChanged()
            })
          }
          data-action="tprm.settings-vendor-registry.a02"
        >
          {busy ? 'Working' : 'Send a test'}
        </button>
        <button
          type="button"
          className="button"
          disabled={busy || !mayWrite}
          onClick={() =>
            void act(async () => {
              const r = await registryApi.resync(s.id)
              setInfo(r.queued + (r.queued === 1 ? ' change' : ' changes') + ' on the way')
              onChanged()
            })
          }
          data-action="tprm.settings-vendor-registry.a03"
        >
          Send everything again
        </button>
        {s.dead > 0 && (
          <button
            type="button"
            className="button"
            disabled={busy || !mayWrite}
            onClick={() =>
              void act(async () => {
                const r = await registryApi.retry(s.id)
                setInfo(r.requeued + (r.requeued === 1 ? ' change' : ' changes') + ' will be tried again')
                onChanged()
              })
            }
            data-action="tprm.settings-vendor-registry.a04"
          >
            Try the failed ones again
          </button>
        )}
        <button
          type="button"
          className="button"
          disabled={busy}
          onClick={() =>
            void act(async () => {
              if (deliveries) {
                setDeliveries(null)
                return
              }
              setDeliveries((await registryApi.deliveries(s.id)).deliveries)
            })
          }
        >
          {deliveries ? 'Hide what was sent' : 'What was sent'}
        </button>
        <button
          type="button"
          className="button ghost"
          disabled={busy || !mayWrite}
          onClick={() =>
            void act(async () => {
              await registryApi.changeSubscriber(s.id, { enabled: !s.enabled })
              onChanged()
            })
          }
          data-action="tprm.settings-vendor-registry.a05"
        >
          {s.enabled ? 'Pause' : 'Resume'}
        </button>
      </div>

      {deliveries && (
        <div className="note">
          {deliveries.length === 0 ? (
            <p>Nothing has been sent to this destination yet</p>
          ) : (
            <ul className="probe-list">
              {deliveries.map((d) => (
                <li
                  key={d.id}
                  className={d.status === 'SENT' ? 'probe-ok' : d.status === 'DEAD' ? 'probe-no' : 'probe-open'}
                >
                  <Status tone={DELIVERY_WORDS[d.status].tone}>{DELIVERY_WORDS[d.status].word}</Status>
                  <span>
                    <strong>{d.vendorName}</strong>{' '}
                    <span className="muted">
                      {DELIVERY_NOUNS[d.eventType]}
                      {d.cause === 'RESYNC' ? ', sent again' : ''}, {when(d.sentAt ?? d.lastAttemptAt ?? d.queuedAt)}
                    </span>
                    {d.status !== 'SENT' && d.lastError ? ' ' + d.lastError : ''}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  )
}

// The keys other systems read the register or ask for a vendor with. They are
// the same machine credentials a buying system holds, issued here with the
// registry's two abilities.
function Keys() {
  const [list, setList] = useState<ProcurementCredential[] | null>(null)
  const [open, setOpen] = useState(false)
  const [name, setName] = useState('')
  const [kind, setKind] = useState<'API_KEY' | 'WEBHOOK_HMAC'>('API_KEY')
  const [read, setRead] = useState(true)
  const [ask, setAsk] = useState(false)
  const [report, setReport] = useState(false)
  const [issued, setIssued] = useState<IssuedProcurementCredential | null>(null)
  const [problem, setProblem] = useState('')
  const save = useSave(open, 'Issue a key')
  const busy = save.busy
  const mayWrite = can('config.write')

  const load = useCallback(() => {
    api
      .procurementCredentials()
      .then((r) =>
        setList(r.credentials.filter((c) => c.scopes.some((s) => (REGISTRY_SCOPES as readonly string[]).includes(s)))),
      )
      .catch((e) => setProblem(describe(e)))
  }, [])
  useEffect(load, [load])

  function issue(e: FormEvent) {
    e.preventDefault()
    setProblem('')
    const scopes = [read ? 'registry.read' : '', ask ? 'vendor.request' : '', report ? 'incident.report' : ''].filter(Boolean)
    save.send(api.createProcurementCredential({ name, kind, scopes }), {
      shown: setIssued,
      done: () => {
        setName('')
        load()
      },
      failed: (err) => setProblem(describe(err)),
      lost: 'The key was issued, but its dialog was closed before the key could be shown, and it cannot be shown again. Revoke it and issue another.',
    })
  }


  function close() {
    setOpen(false)
    setIssued(null)
    setProblem('')
  }

  return (
    <section className="panel">
      <div className="p1-head">
        <div>
          <h3>Keys for other systems</h3>
          <p className="muted">To read the register or ask for a vendor</p>
        </div>
        <div className="p1-actions">
          <button
            type="button"
            className="button"
            disabled={!mayWrite}
            title={mayWrite ? undefined : refusalReason('config.write')}
            onClick={() => setOpen(true)}
          >
            Issue a key
          </button>
        </div>
      </div>
      {problem && !open && <p className="note warn">{problem}</p>}

      <Dialog
        shape="modal"
        kind="form"
        open={open}
        onClose={close}
        title={issued ? 'Copy this key now' : 'Issue a key'}
        busy={busy}
        guard={issued ? 'Close without copying the key? It cannot be shown again.' : undefined}
        more={
          issued ? (
            <button type="button" onClick={close}>
              I have copied it
            </button>
          ) : undefined
        }
        primary={
          issued
            ? undefined
            : {
                label: busy ? 'Issuing' : 'Issue and show it once',
                form: 'registry-key-form',
                off: !read && !ask && !report && 'Tick at least one thing the key may do',
                actionId: 'tprm.settings-vendor-registry.a06',
              }
        }
      >
        {issued ? (
          <div className="int-section" role="status">
            <p className="int-helper">It cannot be shown again</p>
            <p className="mono int-wrap int-secret">
              {issued.apiKey ? issued.apiKey : 'Key id ' + issued.keyId + ', signing secret ' + issued.signingSecret}
            </p>
            <p className="int-prose">{issued.how}</p>
          </div>
        ) : (
          <form id="registry-key-form" className="wizard-form int-form" onSubmit={issue}>
            <label>
              Name
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Group GRC register" required />
            </label>
            <label>
              How it proves itself
              <select value={kind} onChange={(e) => setKind(e.target.value as 'API_KEY' | 'WEBHOOK_HMAC')}>
                <option value="API_KEY">It sends the key</option>
                <option value="WEBHOOK_HMAC">It signs each call</option>
              </select>
            </label>
            <fieldset className="fieldset int-fieldset">
              <legend>It may</legend>
              <label className="check">
                <input type="checkbox" checked={read} onChange={(e) => setRead(e.target.checked)} /> Read the vendor
                register
              </label>
              <label className="check">
                <input type="checkbox" checked={ask} onChange={(e) => setAsk(e.target.checked)} /> Ask for a vendor to
                be added
              </label>
              <label className="check">
                <input type="checkbox" checked={report} onChange={(e) => setReport(e.target.checked)} /> Say that the
                Board, a regulator or the people affected were told about a vendor incident
              </label>
            </fieldset>
            {problem && <p className="note warn">{problem}</p>}
          </form>
        )}
      </Dialog>

      {list && list.length === 0 && <p className="empty">No system holds a key for the register</p>}
      {list && list.length > 0 && (
        <div className="table-wrap">
          <table className="table vr-table">
            <thead>
              <tr>
                <th scope="col">Name</th>
                <th scope="col">May</th>
                <th scope="col" className="vr-wide">
                  Last used
                </th>
                <th scope="col">
                  <span className="visually-hidden">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {list.map((c) => (
                <tr key={c.id}>
                  <td>
                    <strong>{c.name}</strong>
                    <span className="int-sub">{c.kind === 'API_KEY' ? 'Sends the key' : 'Signs each call'}</span>
                  </td>
                  <td>{scopesSentence(c.scopes)}</td>
                  <td className="vr-wide">
                    {c.revokedAt ? (
                      <Status tone="warn">Revoked</Status>
                    ) : c.lastUsedAt ? (
                      shortDate(c.lastUsedAt)
                    ) : (
                      'Not yet'
                    )}
                  </td>
                  <td>
                    {!c.revokedAt && mayWrite && (
                      <button
                        type="button"
                        className="button ghost"
                        onClick={() =>
                          void api
                            .revokeProcurementCredential(c.id)
                            .then(load)
                            .catch((e) => setProblem(describe(e)))
                        }
                        data-action="tprm.settings-vendor-registry.a07"
                      >
                        Revoke
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}
